// Reusable Site layout, wraps every page with header/footer AND manages all
// SEO + AEO (Answer-Engine-Optimisation) head tags via vanilla useEffect.
//
// Why no react-helmet? Avoids a dependency; the manager is ~80 lines and
// emits/cleans up tags by `data-managed` attribute so re-renders don't leak.
//
// Props:
//   title, description       core SEO
//   image                    absolute URL preferred (falls back to default)
//   url                      canonical override (defaults to current location)
//   type                     "website" | "article" | "product"  (og:type)
//   siteName                 default "CoSec Global"
//   locale                   default "en_GB"
//   noindex                  set true on admin / preview pages
//   jsonLd                   raw JSON-LD object | array of objects (article, product, faq…)
//   breadcrumbs              [{name, href}, …]  emits BreadcrumbList JSON-LD
//   faq                      [{q, a}, …]        emits FAQPage JSON-LD (AEO!)
import React, { useEffect } from "react";
import CosecHeader from "@/components/cosec/Header";
import Footer from "@/components/cosec/Footer";

const DEFAULT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://www.cosecglobal.com";

const DEFAULTS = {
  siteName: "CoSec Global",
  description: "One platform for entity management. Corporate governance, simplified. AI-powered, ISO 27001 and ISO 27701 certified.",
  image: `${DEFAULT_ORIGIN}/assets/cosec-logo-blue.png`,
  locale: "en_GB",
  type: "website",
  twitter: "@cosecglobal",
};

// ---- DOM helpers ----------------------------------------------------------
function setMeta(attrs) {
  const sel = Object.entries(attrs.match)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join("");
  let el = document.head.querySelector(`meta${sel}[data-managed="seo"]`);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.match).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute("data-managed", "seo");
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content || "");
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"][data-managed="seo"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-managed", "seo");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function clearManagedJsonLd() {
  document
    .head
    .querySelectorAll('script[type="application/ld+json"][data-managed="seo"]')
    .forEach((s) => s.remove());
}

function emitJsonLd(payload) {
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-managed", "seo");
  s.textContent = JSON.stringify(payload);
  document.head.appendChild(s);
}

// ---- Component ------------------------------------------------------------
export default function SiteLayout({
  title,
  description,
  image,
  url,
  type = "website",
  siteName = DEFAULTS.siteName,
  locale = DEFAULTS.locale,
  noindex = false,
  jsonLd,
  breadcrumbs,
  faq,
  children,
}) {
  const fullTitle = title ? `${title}` : `${DEFAULTS.siteName} | One Platform for Entity Management`;
  const desc = description || DEFAULTS.description;
  const img = image || DEFAULTS.image;
  const canonical = url || (typeof window !== "undefined" ? window.location.href : "");
  const ogType = type === "product" ? "website" : type; // og:type doesn't have "product", map to website

  useEffect(() => {
    // ---- <title> + <link rel="canonical"> ---------------------------------
    document.title = fullTitle;
    if (canonical) setLink("canonical", canonical);

    // ---- Core SEO ---------------------------------------------------------
    setMeta({ match: { name: "description" }, content: desc });
    setMeta({
      match: { name: "robots" },
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });

    // ---- Open Graph (LinkedIn uses these too) -----------------------------
    setMeta({ match: { property: "og:title" }, content: fullTitle });
    setMeta({ match: { property: "og:description" }, content: desc });
    setMeta({ match: { property: "og:image" }, content: img });
    setMeta({ match: { property: "og:image:alt" }, content: fullTitle });
    setMeta({ match: { property: "og:image:width" }, content: "1200" });
    setMeta({ match: { property: "og:image:height" }, content: "630" });
    setMeta({ match: { property: "og:type" }, content: ogType });
    setMeta({ match: { property: "og:locale" }, content: locale });
    setMeta({ match: { property: "og:site_name" }, content: siteName });
    if (canonical) setMeta({ match: { property: "og:url" }, content: canonical });

    // ---- Twitter (X) ------------------------------------------------------
    setMeta({ match: { name: "twitter:card" }, content: "summary_large_image" });
    setMeta({ match: { name: "twitter:title" }, content: fullTitle });
    setMeta({ match: { name: "twitter:description" }, content: desc });
    setMeta({ match: { name: "twitter:image" }, content: img });
    setMeta({ match: { name: "twitter:site" }, content: DEFAULTS.twitter });

    // ---- LinkedIn-friendly extras (it uses og:*, but these help) ----------
    setMeta({ match: { name: "author" }, content: siteName });

    // ---- AEO (Answer Engine Optimisation) ---------------------------------
    // Helps Google AI Overviews, Perplexity, ChatGPT browsing, Bing Copilot
    setMeta({
      match: { name: "ai-content-declaration" },
      content: "human-authored, AI-assisted editorial.",
    });
    setMeta({
      match: { property: "article:publisher" },
      content: "https://www.cosecglobal.com",
    });

    // ---- JSON-LD: fresh on every page change ------------------------------
    clearManagedJsonLd();

    // Always include Organization root entity for AEO
    emitJsonLd({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "CoSec Global",
      url: "https://www.cosecglobal.com",
      logo: DEFAULTS.image,
      sameAs: [
        "https://www.linkedin.com/company/cosecglobal",
        "https://twitter.com/cosecglobal",
      ],
    });

    // WebSite + SearchAction (helps AI search engines understand the site)
    emitJsonLd({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteName,
      url: "https://www.cosecglobal.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://www.cosecglobal.com/kb?cat={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    });

    // Breadcrumbs
    if (breadcrumbs?.length) {
      emitJsonLd({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.href,
        })),
      });
    }

    // FAQPage (massive AEO win, gets surfaced directly in AI Overviews)
    if (faq?.length) {
      emitJsonLd({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    // Caller-supplied JSON-LD (Article / Product / etc.)
    if (jsonLd) {
      (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach((entry) => entry && emitJsonLd(entry));
    }

    // Cleanup on unmount / next route, only the managed nodes
    return () => {
      document.head
        .querySelectorAll('[data-managed="seo"]')
        .forEach((n) => n.remove());
    };
  }, [fullTitle, desc, img, canonical, ogType, locale, siteName, noindex, JSON.stringify(jsonLd), JSON.stringify(breadcrumbs), JSON.stringify(faq)]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <CosecHeader />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}
